import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RecurringTiersEditor — Staffelpreise für wiederkehrende Abos.
 * Discount basiert auf Vertragslaufzeit (Monate). Beispiel: 12 Monate
 * Mindestlaufzeit = 15 % Rabatt auf den Monatspreis.
 */
const RecurringTiersEditor = () => {
  const [tiers, setTiers] = useState([]);
  const [basePrice, setBasePrice] = useState(100);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/recurring-tiers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTiers(res.data.tiers || []);
        setBasePrice(res.data.base_price_per_month ?? 100);
        setActive(res.data.is_active !== false);
      } catch (e) {
        console.error(e);
        toast.error('Fehler beim Laden der Abo-Staffelung');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateTier = (idx, key, value) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t)));
  };

  const addTier = () => {
    const lastMin = tiers.length > 0 ? Number(tiers[tiers.length - 1].min_months || 0) + 1 : 1;
    setTiers((prev) => [
      ...prev,
      { min_months: lastMin, max_months: null, discount_percent: 0, label: `Stufe ${prev.length + 1}` },
    ]);
  };

  const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        tiers: tiers.map((t) => ({
          min_months: Number(t.min_months) || 1,
          max_months: t.max_months === null || t.max_months === '' ? null : Number(t.max_months),
          discount_percent: Number(t.discount_percent) || 0,
          label: t.label || null,
        })),
        base_price_per_month: Number(basePrice) || 0,
        is_active: !!active,
      };
      await axios.put(`${API}/admin/shop/recurring-tiers`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Abo-Staffelung gespeichert');
    } catch (e) {
      console.error(e);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="recurring-tiers-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          Wiederkehrende Abos · Staffelung nach Vertragslaufzeit
        </CardTitle>
        <CardDescription>
          Je länger der Kunde sich bindet, desto günstiger wird der Monatspreis.
          Beispiel: 12 Monate Mindestlaufzeit → 15 % Rabatt auf den Monatspreis.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="recurring-tiers-active"
            />
            <span>Staffelung aktiv</span>
          </label>
          <div className="flex items-center gap-2 text-sm">
            <span>Basis-Monatspreis (CHF):</span>
            <Input
              type="number"
              step="0.5"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              className="w-28"
              data-testid="recurring-tiers-base-price"
            />
          </div>
        </div>

        <div className="space-y-2">
          {tiers.map((tier, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 md:grid-cols-[100px_100px_120px_1fr_60px] gap-2 items-end p-2 rounded-md border bg-gray-50"
              data-testid={`recurring-tier-row-${idx}`}
            >
              <div>
                <label className="text-xs text-gray-600">Min. Monate</label>
                <Input
                  type="number"
                  min="1"
                  value={tier.min_months ?? ''}
                  onChange={(e) => updateTier(idx, 'min_months', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Max. Monate</label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={tier.max_months ?? ''}
                  onChange={(e) => updateTier(idx, 'max_months', e.target.value === '' ? null : e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Rabatt %</label>
                <Input
                  type="number"
                  step="0.5"
                  value={tier.discount_percent ?? 0}
                  onChange={(e) => updateTier(idx, 'discount_percent', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Label</label>
                <Input
                  type="text"
                  placeholder="z. B. Halbjahr"
                  value={tier.label ?? ''}
                  onChange={(e) => updateTier(idx, 'label', e.target.value)}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTier(idx)}
                className="text-red-500 hover:text-red-700"
                data-testid={`recurring-tier-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={addTier} data-testid="recurring-tier-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Stufe hinzufügen
          </Button>
          <Badge variant="secondary">{tiers.length} Stufen</Badge>
        </div>

        <Button onClick={save} disabled={saving} data-testid="recurring-tiers-save-btn">
          {saving ? 'Speichern…' : 'Abo-Staffelung speichern'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RecurringTiersEditor;
